import { useState } from 'react'
import { useController } from 'react-hook-form'

import { useAlertsStore } from '@stores/alertsStore'
import useMutation from '@hooks/useGQLMutation'
import useFormWithErrors from '@hooks/useFormWithErrors'

import { Button } from '@atoms'
import { Textarea } from '@atoms/form/Textarea'
import { AppAlerts } from '@components/molecules/Alerts/AppAlerts'

import { scopedTranslation } from '@utils/I18n'

import { UpdateSupporterDocument } from '@frontend/graphql/mutations/updateSupporter.generated'

const tSupporterShow = scopedTranslation('supporter_show')
const tShared = scopedTranslation('shared.buttons')

const ALERTS_PATH = 'backgroundNote' as const

type FormData = {
  supporter: {
    note: string
  }
}

const useBackgroundNoteForm = (
  supporterId: string,
  currentNote: string | null,
  onSuccess: (note: string) => void
) => {
  const {
    formState: { errors },
    handleSubmit,
    control,
  } = useFormWithErrors<FormData>({
    mode: 'onTouched',
    defaultValues: {
      supporter: { note: currentNote || '' },
    },
  })

  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  const { field } = useController({
    name: 'supporter.note',
    control
  })

  const { mutate } = useMutation(UpdateSupporterDocument)

  const onSubmit = async (values: FormData) => {
    clearAlerts(ALERTS_PATH)

    const mutationData = { 
      id: supporterId, 
      note: values.supporter.note 
    }

    mutate(
      mutationData,
      {
        onSuccess: (response) => {
          if (response.updateSupporter?.success) {
            onSuccess(response.updateSupporter?.supporter?.note ?? '')
            clearAlerts(ALERTS_PATH)
          } else if (response.updateSupporter) {
            setAlerts({
              alertsPath: ALERTS_PATH,
              alerts: response.updateSupporter.errors?.map(error => ({
                type: 'error' as const,
                details: {
                  title: error.message
                }
              })) || []
            })
          }
        },
        onError: () => {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts: [{
              type: 'error' as const,
              details: {
                title: tSupporterShow('background_note.save_error')
              }
            }]
          })
        },
      }
    )
  }

  return {
    field,
    errors,
    handleSubmit: handleSubmit(onSubmit),
  }
}

type BackgroundNoteFieldProps = {
  supporterId: string
  currentNote: string | null
  onCancel: () => void
  onSuccess: (note: string) => void
}

function BackgroundNoteField({
  supporterId,
  currentNote,
  onCancel,
  onSuccess,
}: BackgroundNoteFieldProps) {
  const { field, errors, handleSubmit } = useBackgroundNoteForm(supporterId, currentNote, onSuccess)

  return (
    <div className="tw:col-span-3">
      <form onSubmit={handleSubmit} className="tw:flex tw:flex-col tw:gap-4">
        <AppAlerts alertsPath={ALERTS_PATH} />
        <Textarea
          {...field}
          placeholder={tSupporterShow('background_note.placeholder')}
          rows={4}
          errors={errors}
        />
        <div className="tw:flex tw:gap-2">
          <Button type="submit" size="sm" aria-label={tSupporterShow('background_note.aria_labels.save')}>
            {tShared('save')}
          </Button>
          <Button type="button" rank="secondary" size="sm" onClick={onCancel} aria-label={tSupporterShow('background_note.aria_labels.cancel')}>
            {tShared('cancel')}
          </Button>
        </div>
      </form>
    </div>
  )
}

function BackgroundNoteDisplay({ note }: { note: string | null }) {
  if (!note) {
    return (
      <div className="tw:text-gray-400">
        {tSupporterShow('background_note.empty_state')}
      </div>
    )
  }

  return (
    <div className="tw:whitespace-pre-wrap tw:text-gray-600">
      {note}
    </div>
  )
}

type BackgroundNoteFormProps = {
  supporterId: string
  initialNote: string | null
}

function BackgroundNoteForm({ supporterId, initialNote }: BackgroundNoteFormProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [currentNote, setCurrentNote] = useState(initialNote)

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleSuccess = (newNote: string) => {
    setCurrentNote(newNote)
    setIsEditing(false)
  }

  return (
    <section
      data-testid="background-note-form"
      className="tw:flex tw:flex-col tw:px-8 tw:shadow-xs tw:rounded-xl tw:border tw:border-gray-200"
      data-compid="supporter-show_background-note-form"
    >
      <div className="tw:grid tw:grid-cols-5 tw:grid-rows-1 tw:gap-4 tw:items-center tw:py-3">
        <div>
          <p className="tw:font-medium">{tSupporterShow('background_note.title')}</p>
        </div>
        {isEditing ? (
          <BackgroundNoteField
            supporterId={supporterId}
            currentNote={currentNote}
            onCancel={handleCancel}
            onSuccess={handleSuccess}
          />
        ) : (
          <div className="tw:col-span-3">
            <BackgroundNoteDisplay note={currentNote} />
          </div>
        )}

        <div className="tw:col-start-5 tw:flex tw:justify-end">
          <Button
            type="button"
            rank="secondary"
            onClick={handleEdit}
            leadingIcon="pencil-02"
            disabled={isEditing}
            aria-label={tSupporterShow('background_note.aria_labels.edit')}
          >
            {tShared('edit')}
          </Button>
        </div>
      </div>
    </section>
  )
}

export { BackgroundNoteForm }